"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { toast } from "react-hot-toast";
import { ExchangeCurrency } from "@prisma/client";
import { SafeUser } from "@/app/types";
import type { BeginnerAlternative } from "@/app/libs/beginnerAlternatives";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import convertPrice from "@/app/utils/CurrencyConverter";
import formatPrice from "@/app/utils/formatPrice";

interface BeginnerAlternativesProps {
  alternatives: BeginnerAlternative[];
  /** Dive center of the listing being booked. */
  diveCenterId?: string | null;
  currentUser?: SafeUser | null;
  rates: ExchangeCurrency | null;
  /** Moves the booking to the suggested listing (checkout, or its date picker). */
  onBook?: (alternative: BeginnerAlternative) => Promise<void>;
  /** Overrides the default "No certification yet?..." heading (listing page uses "Not certified yet?"). */
  heading?: string;
  /** Bigger heading + border, for the listing page (the booking form keeps the compact look). */
  prominent?: boolean;
}

/** Best beginner listing suggested under the "needs a certification" error. */
const BeginnerAlternatives: React.FC<BeginnerAlternativesProps> = ({
  alternatives,
  diveCenterId,
  currentUser,
  rates,
  onBook,
  heading: headingOverride,
  prominent = false,
}) => {
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const targetCurrency = selectedCurrency?.code || currentUser?.currency || null;
  const alternative = alternatives[0];
  const [price, setPrice] = useState<{ amount: number; currency: string } | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    if (!alternative) return;
    let cancelled = false;
    const run = async () => {
      const fromCurrency = alternative.currency || "USD";
      const toCurrency = targetCurrency || fromCurrency;
      const amount =
        toCurrency === fromCurrency
          ? alternative.price
          : await convertPrice(alternative.price, fromCurrency, toCurrency, rates);
      if (!cancelled) setPrice({ amount, currency: toCurrency });
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [alternative, targetCurrency, rates]);

  if (!alternative) return null;

  const heading =
    headingOverride ??
    (alternative.diveCenterId === diveCenterId && alternative.diveCenterName
      ? `No certification yet? ${alternative.diveCenterName} offers:`
      : "No certification yet? Beginner option nearby:");
  const shownPrice = price ?? {
    amount: alternative.price,
    currency: alternative.currency || "USD",
  };

  const handleBook = async () => {
    if (isSwitching) return;
    if (!onBook) {
      window.location.href = `/listings/${alternative.id}`;
      return;
    }
    setIsSwitching(true);
    try {
      await onBook(alternative);
    } catch (error) {
      console.error("Beginner switch failed:", error);
      toast.error("Could not open this booking. Please try again.");
      setIsSwitching(false);
    }
  };

  return (
    <div
      className={`mt-3 rounded-xl bg-cyan-50/60 p-3 ${
        prominent ? "border-2 border-cyan-500" : "border border-cyan-100"
      }`}
    >
      <p
        className={`text-neutral-900 mb-2 ${
          prominent ? "text-lg font-bold" : "text-sm font-semibold"
        }`}
      >
        {heading}
      </p>
      <button
        type="button"
        onClick={handleBook}
        disabled={isSwitching}
        className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white p-2 text-left shadow-sm transition hover:border-cyan-300 hover:shadow-md disabled:cursor-wait disabled:opacity-80"
      >
        <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg bg-gray-200">
          <Image
            src={alternative.imageSrc || "/images/no-photo-yet.jpg"}
            alt={alternative.title}
            fill
            sizes="80px"
            quality={60}
            className="object-cover transition group-hover:scale-110"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold text-neutral-900">
            {alternative.title}
          </p>
          {alternative.diveCenterName && (
            <p className="truncate text-xs text-neutral-500">
              {alternative.diveCenterName}
            </p>
          )}
          <p className="text-xs text-neutral-700 mt-0.5">
            from{" "}
            <span className="font-bold text-neutral-900">
              {formatPrice(shownPrice.amount)} {shownPrice.currency}
            </span>
          </p>
        </div>
        <span className="shrink-0 rounded-lg bg-cyan-700 px-3 py-2 text-xs font-semibold text-white transition group-hover:bg-cyan-800">
          {isSwitching ? "Opening..." : "Book this instead"}
        </span>
      </button>
    </div>
  );
};

export default BeginnerAlternatives;
